import Cookies from 'js-cookie';

/**
 * Enum representing the available storage mechanisms,
 * ordered by priority (highest to lowest).
 */
export enum StorageType {
  LocalStorage = 'localStorage',
  SessionStorage = 'sessionStorage',
  Cookie = 'cookie',
}

/** Options for cookie storage */
export interface CookieOptions {
  /** Number of days until the cookie expires. Defaults to 365. */
  expires?: number;
  /** Cookie path. Defaults to '/'. */
  path?: string;
  /** Whether the cookie should only be sent over HTTPS. */
  secure?: boolean;
  /** SameSite attribute. Defaults to 'Lax'. */
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/** Configuration for StoreProvider */
export interface StoreProviderConfig {
  /** Override the default priority order */
  priority?: StorageType[];
  /** Default cookie options when falling back to cookies */
  cookieOptions?: CookieOptions;
}

const DEFAULT_PRIORITY: StorageType[] = [
  StorageType.LocalStorage,
  StorageType.SessionStorage,
  StorageType.Cookie,
];

const DEFAULT_COOKIE_OPTIONS: CookieOptions = {
  expires: 365,
  path: '/',
  sameSite: 'Lax',
};

/** Prefix applied to every key to namespace StoreProvider entries. */
const KEY_PREFIX = 'ui_connect__';

/**
 * StoreProvider — a static utility class that persists JSON-serializable objects
 * across the best available browser storage mechanism.
 *
 * **Fallback priority (default):**
 * 1. `localStorage`  — persistent, ~5-10 MB quota
 * 2. `sessionStorage` — tab-scoped, ~5 MB quota
 * 3. `cookies`        — 4 KB limit, sent with every HTTP request
 *
 * The provider automatically detects which storage is available and uses
 * the highest-priority one that works. If a write fails (e.g. quota exceeded),
 * it falls through to the next available mechanism.
 *
 * @example
 * ```ts
 * StoreProvider.setItem('user', { name: 'John', age: 30 });
 * const user = StoreProvider.getItem<{ name: string; age: number }>('user');
 * StoreProvider.removeItem('user');
 * ```
 */
class StorageProvider {
  private static priority: StorageType[] = DEFAULT_PRIORITY;
  private static cookieOptions: CookieOptions = DEFAULT_COOKIE_OPTIONS;

  /** Prefix a raw key with the app namespace. */
  private static prefixKey(key: string): string {
    return `${KEY_PREFIX}${key}`;
  }

  /** Configure priority order and/or default cookie options. */
  static configure(config: StoreProviderConfig): void {
    if (config.priority) {
      StorageProvider.priority = config.priority;
    }
    if (config.cookieOptions) {
      StorageProvider.cookieOptions = {
        ...DEFAULT_COOKIE_OPTIONS,
        ...config.cookieOptions,
      };
    }
  }

  /** Reset configuration to defaults. */
  static resetConfig(): void {
    StorageProvider.priority = DEFAULT_PRIORITY;
    StorageProvider.cookieOptions = DEFAULT_COOKIE_OPTIONS;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Persist a JSON-serializable value under `key`.
   * Tries each storage in priority order; falls through on failure.
   *
   * @returns The `StorageType` that was used, or `null` if all failed.
   */
  static setItem<T = unknown>(key: string, value: T): StorageType | null {
    const prefixed = StorageProvider.prefixKey(key);
    const serialized = JSON.stringify(value);

    for (const type of StorageProvider.priority) {
      try {
        switch (type) {
          case StorageType.LocalStorage:
            window.localStorage.setItem(prefixed, serialized);
            return type;

          case StorageType.SessionStorage:
            window.sessionStorage.setItem(prefixed, serialized);
            return type;

          case StorageType.Cookie:
            Cookies.set(prefixed, serialized, StorageProvider.cookieOptions);
            return type;
        }
      } catch {
        // Storage unavailable or quota exceeded — try next.
        continue;
      }
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[StoreProvider] Failed to store key "${key}" in any storage.`
    );
    return null;
  }

  /**
   * Retrieve and deserialize the value stored under `key`.
   * Searches through all priority storages until a value is found.
   *
   * @returns The parsed object, or `null` if not found anywhere.
   */
  static getItem<T = unknown>(key: string): T | null {
    const prefixed = StorageProvider.prefixKey(key);

    for (const type of StorageProvider.priority) {
      try {
        let raw: string | null | undefined;

        switch (type) {
          case StorageType.LocalStorage:
            raw = window.localStorage.getItem(prefixed);
            break;

          case StorageType.SessionStorage:
            raw = window.sessionStorage.getItem(prefixed);
            break;

          case StorageType.Cookie:
            raw = Cookies.get(prefixed) ?? null;
            break;
        }

        if (raw !== null && raw !== undefined) {
          return JSON.parse(raw) as T;
        }
      } catch {
        // Storage unavailable or corrupt data — try next.
        continue;
      }
    }

    return null;
  }

  /**
   * Remove a key from **all** available storages to ensure
   * no stale data remains after a fallback transition.
   */
  static removeItem(key: string): void {
    const prefixed = StorageProvider.prefixKey(key);

    for (const type of StorageProvider.priority) {
      try {
        switch (type) {
          case StorageType.LocalStorage:
            window.localStorage.removeItem(prefixed);
            break;

          case StorageType.SessionStorage:
            window.sessionStorage.removeItem(prefixed);
            break;

          case StorageType.Cookie:
            Cookies.remove(prefixed, {
              path: StorageProvider.cookieOptions.path,
            });
            break;
        }
      } catch {
        // Ignore — already cleaned or unavailable.
      }
    }
  }

  /**
   * Clear **all** data from every available storage.
   * ⚠️ Use with caution — this wipes everything, not just keys set by StoreProvider.
   */
  static clear(): void {
    for (const type of StorageProvider.priority) {
      try {
        switch (type) {
          case StorageType.LocalStorage:
            window.localStorage.clear();
            break;

          case StorageType.SessionStorage:
            window.sessionStorage.clear();
            break;

          case StorageType.Cookie: {
            // Remove all cookies accessible on this path
            const allCookies = Cookies.get();
            Object.keys(allCookies).forEach((name) => {
              Cookies.remove(name, {
                path: StorageProvider.cookieOptions.path,
              });
            });
            break;
          }
        }
      } catch {
        // Ignore — unavailable.
      }
    }
  }

  /**
   * Check which storage type is currently available (highest priority).
   *
   * @returns The first available `StorageType`, or `null` if none work.
   */
  static getAvailableStorage(): StorageType | null {
    for (const type of StorageProvider.priority) {
      if (StorageProvider.isStorageAvailable(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Check if a specific storage mechanism is available and writable.
   */
  static isStorageAvailable(type: StorageType): boolean {
    const testKey = '__StoreProvider_test__';

    try {
      switch (type) {
        case StorageType.LocalStorage:
          window.localStorage.setItem(testKey, '1');
          window.localStorage.removeItem(testKey);
          return true;

        case StorageType.SessionStorage:
          window.sessionStorage.setItem(testKey, '1');
          window.sessionStorage.removeItem(testKey);
          return true;

        case StorageType.Cookie:
          Cookies.set(testKey, '1');
          const exists = Cookies.get(testKey) === '1';
          Cookies.remove(testKey);
          return exists;
      }
    } catch {
      return false;
    }
  }
}

export default StorageProvider;
